import React from 'react';
import { FooterModel } from '@/types/footer';

interface FooterServerProps {
  initialData: FooterModel;
  dict: any;
  currentLang: string;
}

function FooterServer({ initialData, dict, currentLang }: FooterServerProps) {
  return (
    <footer 
      className="border-t border-gray-600"
      style={{ 
        backgroundColor: '#203F72',
        color: initialData.layout.textColor,
        padding: initialData.layout.padding
      }}
    >
      <div className={`max-w-7xl mx-auto px-4 ${initialData.styling.container}`}>
        {/* Partners Section */}
        {initialData.partners && (
          <div className="text-center py-8 border-b border-gray-600 mb-8">
            <h3 className="text-heading-lg font-semibold mb-6 uppercase tracking-wide" style={{ color: '#FFFFFF' }}>
              {initialData.partners.title}
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {initialData.partners.logos.map((logo) => (
                <div key={logo.id} className="flex items-center justify-center">
                  <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center p-2 shadow-md hover:shadow-lg transition-shadow duration-200">
                    <img 
                      src={logo.src}
                      alt={logo.alt}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="mb-4">
              <img 
                src={initialData.company.logo.src}
                alt={initialData.company.logo.alt}
                className="h-12 w-auto"
              />
            </div>
            <p className="text-body-md leading-relaxed" style={{ color: '#FFFFFF' }}>
              {initialData.company.description}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-heading-lg font-semibold mb-4" style={{ color: '#FFFFFF' }}>
              {dict.footer_quickLinks || 'Quick Links'}
            </h4>
            <ul className="space-y-2">
              {initialData.links.quickLinks.map((link) => (
                <li key={link.id}>
                  <a 
                    href={link.href}
                    className="text-body-md hover:text-gray-300 transition-colors duration-200 block"
                    style={{ color: '#FFFFFF' }}
                  >
                    {dict[link.text] || link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us */}
          <div className="space-y-4">
            <h4 className="text-heading-lg font-semibold mb-4" style={{ color: '#FFFFFF' }}>
              {dict.footer_followUs || 'Follow Us'}
            </h4>
            <div className="flex flex-wrap gap-4">
              {initialData.social.platforms.map((platform) => (
                <a
                  key={platform.id}
                  href={platform.href}
                  className="flex items-center space-x-2 text-body-md hover:text-gray-300 transition-colors duration-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <span className="text-gray-700 text-xs font-bold">
                      {platform.icon === 'youtube' ? '▶' : 
                       platform.icon === 'facebook' ? 'f' :
                       platform.icon === 'twitter' ? '🐦' : '📷'}
                    </span>
                  </div>
                  <span className="text-body-md" style={{ color: '#FFFFFF' }}>{dict[platform.name] || platform.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Contact Us */}
          <div className="space-y-4">
            <h4 className="text-heading-lg font-semibold mb-4" style={{ color: '#FFFFFF' }}>
              {dict.footer_contactUs || 'Contact Us'}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <span style={{ color: '#FFFFFF' }}>📞</span>
                <span className="text-body-md" style={{ color: '#FFFFFF' }}>{initialData.contact.phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span style={{ color: '#FFFFFF' }}>✉️</span>
                <span className="text-body-md" style={{ color: '#FFFFFF' }}>{initialData.contact.email}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span style={{ color: '#FFFFFF' }}>📍</span>
                <span className="text-body-md" style={{ color: '#FFFFFF' }}>{initialData.contact.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-600">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-body-md" style={{ color: '#FFFFFF' }}>
              {initialData.legal.copyright}
            </div>
            <div className="flex space-x-6">
              <a 
                href="#" 
                className="text-body-md hover:text-gray-300 transition-colors duration-200"
                style={{ color: '#FFFFFF' }}
              >
                {dict[initialData.legal.terms] || initialData.legal.terms}
              </a>
              <a 
                href="#" 
                className="text-body-md hover:text-gray-300 transition-colors duration-200"
                style={{ color: '#FFFFFF' }}
              >
                {dict[initialData.legal.privacy] || initialData.legal.privacy}
              </a>
              <a 
                href="#" 
                className="text-body-md hover:text-gray-300 transition-colors duration-200"
                style={{ color: '#FFFFFF' }}
              >
                {dict[initialData.legal.cookies] || initialData.legal.cookies}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FooterServer;